import { ButtonBorderless } from "@nova/component-btn"
import classNames from "classnames"
import { LegendItem } from "plugins/PDFv2/PDFLegend/LegendItems/useLegendItems"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useDrag, useDrop } from "react-dnd"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectIdRenaming,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import {
    selectOrderedItemIndexes,
    selectSecondaryStats,
    selectShowTextFeatures,
    selectVisibleStatItems,
    setLegendRenameTimestamp,
    setOrderedItemIndexes,
    toggleSecondaryStats,
    toggleShowTextFeatures,
    toggleVisibleStats,
} from "plugins/PDFv2/PDFSlice"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"

export const PDFFeatures = ({
    toggle,
    enabledLegendItems,
    excludedLegendItems,
    maxLegendItems,
    hasExceededMax,
}: {
    toggle: (id: string) => void
    legendItems: LegendItem[]
    enabledLegendItems: LegendItem[]
    excludedLegendItems: LegendItem[]
    maxLegendItems: number
    hasExceededMax: boolean
}) => {
    const dispatch = useAppDispatch()
    const textFeaturesActive = useAppSelector(selectShowTextFeatures())
    const orderedItemIndexes = useAppSelector(selectOrderedItemIndexes())
    const orderedItems = useMemo(
        () =>
            enabledLegendItems.sort((a, b) => {
                const indexA = orderedItemIndexes[a.id]
                const indexB = orderedItemIndexes[b.id]
                return indexA - indexB
            }),
        [orderedItemIndexes, enabledLegendItems]
    )

    const moveItem = (dragIndex: number, hoverIndex: number) => {
        const updated = [...orderedItems]
        const [removed] = updated.splice(dragIndex, 1)
        updated.splice(hoverIndex, 0, removed)
        const newIndexes: Record<string, number> = {}
        updated.forEach((item, idx) => (newIndexes[item.id] = idx))
        dispatch(setOrderedItemIndexes(newIndexes))
    }

    const excludeAll = useCallback(() => {
        enabledLegendItems.forEach((item) => toggle(item.id))
    }, [enabledLegendItems, toggle])

    const includeAll = useCallback(() => {
        excludedLegendItems.forEach((item) => toggle(item.id))
    }, [excludedLegendItems, toggle])

    const [{ isOver: isIncludedOver }, includedDropRef] = useDrop(
        () => ({
            accept: "excluded",
            drop: (item: { id: string }) => toggle(item.id),
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
            }),
        }),
        [toggle]
    )

    const [{ isOver: isExcludedOver }, excludedDropRef] = useDrop(
        () => ({
            accept: "included",
            drop: (item: { id: string }) => toggle(item.id),
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
            }),
        }),
        [toggle]
    )

    const includedRef = useRef<HTMLDivElement>(null)
    includedDropRef(includedRef)

    const excludedRef = useRef<HTMLDivElement>(null)
    excludedDropRef(excludedRef)

    return (
        <>
            <InspectorPanelKeyValueToggle
                keyTitle="Show text layers"
                isToggled={textFeaturesActive}
                setIsToggled={() => dispatch(toggleShowTextFeatures())}
                toggleClassName="justify-between"
                className="mt-2 pb-2 border-b border-b-gray-700"
            />
            <div
                ref={includedRef}
                className={classNames("border border-transparent p-2", {
                    "rounded-lg !border-blue-500": isIncludedOver,
                })}
            >
                <div className="flex items-center justify-between">
                    <small className="text-gray-0">INCLUDED</small>
                    <small
                        className={classNames("text-gray-300", { "!text-red-500": hasExceededMax })}
                    >{`${enabledLegendItems.length} features (${maxLegendItems} max)`}</small>
                </div>
                <ButtonBorderless
                    title="Exclude all"
                    className="-translate-x-3 mt-2"
                    onClick={excludeAll}
                />
                <div className="flex flex-col w-full">
                    {orderedItems.map((item, index) => {
                        return (
                            <FeatureItem
                                item={item}
                                toggle={toggle}
                                legendItems={orderedItems}
                                key={item.id}
                                index={index}
                                moveItem={moveItem}
                            />
                        )
                    })}
                </div>
            </div>
            <div className="my-3 border-b border-gray-700" />
            <div
                ref={excludedRef}
                className={classNames("border border-transparent p-2", {
                    "rounded-lg !border-blue-500": isExcludedOver,
                })}
            >
                <div className="flex items-center justify-between">
                    <small className="text-gray-0">EXCLUDED</small>
                    <small className="text-gray-300">{`${excludedLegendItems.length} features`}</small>
                </div>
                <ButtonBorderless
                    title="Include all"
                    className="-translate-x-3 mt-2"
                    onClick={includeAll}
                />
                <div className="flex flex-col w-full">
                    {excludedLegendItems.map((item, index) => {
                        return (
                            <FeatureItem
                                item={item}
                                toggle={toggle}
                                isExcluded={true}
                                className="text-gray-300"
                                key={item.id}
                                index={index}
                                moveItem={moveItem}
                                legendItems={excludedLegendItems}
                            />
                        )
                    })}
                </div>
            </div>
        </>
    )
}

export const featureNameKey = "renamedLegendItems"
export const FeatureItem = ({
    item,
    className,
    toggle,
    isExcluded,
    legendItems,
    index,
    moveItem,
}: {
    item: LegendItem
    className?: string
    toggle: (id: string) => void
    isExcluded?: boolean
    legendItems: LegendItem[]
    index: number
    moveItem: (dragIndex: number, hoverIndex: number) => void
}) => {
    const { id, title, stat, secondaryStat } = item
    const dispatch = useAppDispatch()
    const [isHovering, setIsHovering] = useState(false)

    const activePdfContextMenu = useAppSelector(selectActiveContextMenu("PDF"))
    const idRenaming = useAppSelector(selectIdRenaming())
    const idRenamingRef = useRef(idRenaming)
    const layerNameInputRef = useRef<HTMLInputElement>(null)

    const visibleStats = useAppSelector(selectVisibleStatItems())
    const visibleSecondaryStats = useAppSelector(selectSecondaryStats())

    const isStatVisible = visibleStats[id]
    const isSecondaryStatVisible = visibleSecondaryStats[id]

    const isRenaming = idRenaming === `pdf-legend-feature-${id}`

    const localRename = JSON.parse(localStorage.getItem(featureNameKey) || "{}")[id] as string

    const dropdownButtons: ContextMenuItem[][] = useMemo(() => {
        const buttons: ContextMenuItem[] = [
            {
                title: "Rename",
                onClick: () => dispatch(setIdRenaming(`pdf-legend-feature-${id}`)),
                Icon: Icons.Pencil,
            },
        ]
        if (stat) {
            const isStatSetByDefault = stat.isSetByDefault
            const isShowingStat = isStatVisible ?? (isStatSetByDefault ? true : false)

            buttons.push({
                title: `${isShowingStat ? "Hide" : "Show"} ${stat.label}`,
                onClick: () =>
                    dispatch(toggleVisibleStats({ id, defaultValue: isStatSetByDefault ?? false })),
                Icon: Icons.Chart,
            })
        }
        if (secondaryStat) {
            buttons.push({
                title: `${isSecondaryStatVisible ? "Hide" : "Show"} ${secondaryStat.label}`,
                onClick: () => dispatch(toggleSecondaryStats(id)),
                Icon: Icons.AddChart,
            })
        }
        const bottom = [
            {
                title: isExcluded ? "Include" : "Exclude",
                onClick: () => toggle(id),
                Icon: Icons.RemoveSelection,
            },
            {
                title: "Move to Top",
                onClick: () => {
                    const dragIndex = legendItems.findIndex((i) => i.id === id)
                    moveItem(dragIndex, 0)
                },
                Icon: Icons.DoubleUpArrow,
            },
            {
                title: "Move to Bottom",
                onClick: () => {
                    const dragIndex = legendItems.findIndex((i) => i.id === id)
                    moveItem(dragIndex, legendItems.length)
                },
                Icon: Icons.DoubleDownArrow,
            },
        ]

        return [[...buttons, ...bottom]]
    }, [
        id,
        stat,
        secondaryStat,
        isStatVisible,
        isSecondaryStatVisible,
        isExcluded,
        legendItems,
        moveItem,
        toggle,
        dispatch,
    ])

    const [{}, dragConnector] = useDrag(
        () => ({
            type: isExcluded ? "excluded" : "included",
            item: { id },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
        }),
        [isExcluded]
    )

    const [{ isOver }, dropConnector] = useDrop(
        () => ({
            accept: ["included"],
            drop: (item: { id: string }) => {
                // Find the current index of the dragged item based on its id
                const dragIndex = legendItems.findIndex((i) => i.id === item.id)
                const hoverIndex = index

                if (dragIndex === -1 || dragIndex === hoverIndex) return

                const targetIndex = dragIndex < hoverIndex ? hoverIndex : hoverIndex + 1
                moveItem(dragIndex, targetIndex)
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
            }),
            canDrop: () => !isExcluded,
        }),
        [index, legendItems]
    )

    const dragRef = useRef<HTMLDivElement>(null)
    dragConnector(dragRef)

    const dropRef = useRef<HTMLDivElement>(null)
    dropConnector(dropRef)

    useEffect(() => {
        idRenamingRef.current = idRenaming

        if (idRenaming?.startsWith("pdf-legend-feature")) layerNameInputRef.current?.focus()
    }, [idRenaming])

    const shouldShow3DotsButton =
        (isHovering || activePdfContextMenu?.data.pdfId === id) && !isRenaming

    //we need legend renaming to persist, but we dont want to store it, so it chills in local storage
    const handleFeatureRename = useCallback(
        (rename: string) => {
            const currentRenames = JSON.parse(
                localStorage.getItem(featureNameKey) || "{}"
            ) as Record<string, string>
            const newRenames = { ...currentRenames, [id]: rename }

            localStorage.setItem(featureNameKey, JSON.stringify(newRenames))
            dispatch(setLegendRenameTimestamp(new Date().getTime()))
        },
        [id, dispatch]
    )

    const removeLocalRename = useCallback(() => {
        const currentRenames = JSON.parse(localStorage.getItem(featureNameKey) || "{}") as Record<
            string,
            string
        >
        delete currentRenames[id]
        localStorage.setItem(featureNameKey, JSON.stringify(currentRenames))
    }, [id])

    return (
        <div
            className={classNames("my-2 relative border-b border-b-transparent", {
                "!border-blue-500": isOver && !isExcluded,
            })}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <div className="flex justify-between items-center w-full" ref={dropRef}>
                <div className="flex items-center gap-2.5 min-w-0">
                    <div ref={dragRef}>
                        <Icons.SmallIcon
                            Icon={Icons.DragLight}
                            className={classNames("hover:!text-gray-0 cursor-grab", {
                                "text-gray-500": !isHovering,
                                "text-gray-100": isHovering,
                            })}
                        />
                    </div>
                    <RenamableText
                        ref={layerNameInputRef}
                        Tag="small"
                        title={localRename || title}
                        id="pdf-legend-feature"
                        className={classNames(
                            "text-gray-0 cursor-text rounded-md text-ellipsis overflow-hidden whitespace-nowrap",
                            className
                        )}
                        isRenaming={isRenaming}
                        isFullWidth={true}
                        shouldHighlightOnHover={true}
                        shouldMakeTextSelectable={false}
                        initialTitleIsPlaceholder={false}
                        onClick={() => {
                            dispatch(setIdRenaming(`pdf-legend-feature-${id}`))
                        }}
                        onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                        onConfirmRenaming={(_, name) => {
                            dispatch(setIdRenaming(undefined))
                            handleFeatureRename(name)
                        }}
                        onResetName={() => {
                            dispatch(setIdRenaming(undefined))
                            removeLocalRename()
                        }}
                    />
                </div>
                <div className="relative">
                    {isExcluded ? (
                        <Icons.SmallIcon
                            Icon={Icons.Plus}
                            onClick={() => toggle(id)}
                            className={classNames("cursor-pointer invisible hover:text-gray-0", {
                                "!visible": isHovering,
                            })}
                        />
                    ) : (
                        shouldShow3DotsButton && (
                            <Icons.SmallIcon
                                Icon={Icons.ThreeDotsSettings}
                                className={classNames(
                                    "cursor-pointer text-gray-300 hover:text-gray-0"
                                )}
                                onClick={() => {
                                    dispatch(
                                        setActiveContextMenu(
                                            activePdfContextMenu?.data.pdfId === id
                                                ? undefined
                                                : {
                                                      type: "PDF",
                                                      data: {
                                                          pdfId: id,
                                                      },
                                                      position: {
                                                          top: 0,
                                                          left: 0,
                                                      },
                                                  }
                                        )
                                    )
                                }}
                            />
                        )
                    )}
                    {activePdfContextMenu?.data.pdfId === id && (
                        <ContextMenu
                            sections={dropdownButtons}
                            onBlur={() => dispatch(setActiveContextMenu(undefined))}
                            style={{ transform: "translateX(calc(-100% + 20px))" }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
